import { AxiosInstance, AxiosResponse } from 'axios';
import {
  AddStockRequest,
  AdjustStockRequest,
  InventoryBatch,
  InventoryOperationResponse,
  InventoryTransaction,
  PaginatedResponse,
  RemoveStockRequest,
  SearchParams,
} from '../../types';

/** Inventory mutation and batch history API calls. */
export class InventoryApi {
  constructor(private readonly http: AxiosInstance) {}

  async addStock(itemId: string, request: AddStockRequest): Promise<InventoryOperationResponse> {
    const response: AxiosResponse<InventoryOperationResponse> = await this.http.post(
      `/inventory/items/${itemId}/add-stock`, request
    );
    return response.data;
  }

  async removeStock(itemId: string, request: RemoveStockRequest): Promise<InventoryOperationResponse> {
    const response: AxiosResponse<InventoryOperationResponse> = await this.http.post(
      `/inventory/items/${itemId}/remove-stock`, request
    );
    return response.data;
  }

  async adjustStock(itemId: string, request: AdjustStockRequest): Promise<InventoryOperationResponse> {
    const response: AxiosResponse<InventoryOperationResponse> = await this.http.post(
      `/inventory/items/${itemId}/adjust-stock`, request
    );
    return response.data;
  }

  async deleteBatch(batchId: string): Promise<InventoryOperationResponse> {
    const response: AxiosResponse<InventoryOperationResponse> = await this.http.delete(`/inventory/batches/${batchId}`);
    return response.data;
  }

  async toggleBatchTracking(itemId: string, enableBatchTracking: boolean, batchNumber?: string): Promise<InventoryOperationResponse> {
    const params = new URLSearchParams({ enableBatchTracking: String(enableBatchTracking) });
    if (batchNumber?.trim()) {
      params.set('batchNumber', batchNumber.trim());
    }
    const response: AxiosResponse<InventoryOperationResponse> = await this.http.post(
      `/inventory/items/${itemId}/toggle-batch-tracking?${params.toString()}`
    );
    return response.data;
  }

  async getInventoryBatches(itemId: string): Promise<InventoryBatch[]> {
    const response: AxiosResponse<InventoryBatch[]> = await this.http.get(`/inventory/items/${itemId}/batches`);
    return response.data;
  }

  async getInventoryBatchesPaginated(itemId: string, params: SearchParams): Promise<PaginatedResponse<InventoryBatch>> {
    const response: AxiosResponse<PaginatedResponse<InventoryBatch>> = await this.http.get(
      `/inventory/items/${itemId}/batches/paginated`, { params }
    );
    return response.data;
  }

  async getInventoryTransactions(itemId: string): Promise<InventoryTransaction[]> {
    const response: AxiosResponse<InventoryTransaction[]> = await this.http.get(`/inventory/items/${itemId}/transactions`);
    return response.data;
  }

  async getInventoryTransactionsPaginated(itemId: string, params: SearchParams): Promise<PaginatedResponse<InventoryTransaction>> {
    const response: AxiosResponse<PaginatedResponse<InventoryTransaction>> = await this.http.get(
      `/inventory/items/${itemId}/transactions/paginated`, { params }
    );
    return response.data;
  }

  async getExpiredBatches(limit?: number, signal?: AbortSignal): Promise<InventoryBatch[]> {
    const requestConfig = limit !== undefined || signal
      ? { ...(limit !== undefined ? { params: { limit } } : {}), ...(signal ? { signal } : {}) }
      : undefined;
    const response: AxiosResponse<InventoryBatch[]> = requestConfig
      ? await this.http.get('/inventory/expired-batches', requestConfig)
      : await this.http.get('/inventory/expired-batches');
    return response.data;
  }

  async getExpiringSoonBatches(daysThreshold = 30, limit?: number, signal?: AbortSignal): Promise<InventoryBatch[]> {
    const requestConfig = limit !== undefined || signal
      ? { ...(limit !== undefined ? { params: { limit } } : {}), ...(signal ? { signal } : {}) }
      : undefined;
    const url = '/inventory/expiring-soon-batches?daysThreshold=' + daysThreshold;
    const response: AxiosResponse<InventoryBatch[]> = requestConfig
      ? await this.http.get(url, requestConfig)
      : await this.http.get(url);
    return response.data;
  }
}
